import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ANIMATION_DURATION } from "@/constants/animations.constants";
import { useConfetti } from "@/hooks";
import { MobileViewportContainer } from "@/modules/onboarding/components/MobileViewportContainer/MobileViewportContainer";
import { OnboardingBackButton } from "@/modules/onboarding/components/OnboardingBackButton/OnboardingBackButton";
import { RewardProgressBar } from "@/modules/onboarding/components/RewardProgressBar/RewardProgressBar";
import { LocalStorageService } from "@/modules/storage";
import { useUser } from "@/modules/user";
import { get } from "api";
import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import GiftboxV3 from "atoms/icon/icons/giftbox_v3.svg";

import { V3ShareFriendsPage_URL } from "./V3ShareFriendsPage";

export default function V3RewardsPage() {
  const navigate = useNavigate();
  const { user } = useUser();
  const [progress, setProgress] = useState(0);
  const [showReward, setShowReward] = useState(false);

  const {
    data: activeSignupReward,
    isLoading: isActiveSignupRewardLoading,
    isError: isActiveSignupRewardError,
  } = useQuery({
    queryKey: ["/incentives/{platformLocationId}/active-signup-reward"],
    queryFn: () =>
      get("/incentives/{platformLocationId}/active-signup-reward", {
        platformLocationId: user?.platformLocation?.id ?? "",
      }),
    enabled: !!user?.platformLocation?.id,
    retry: false, // Don't retry on 404
  });

  const { data: platform, isLoading: isPlatformLoading } = useQuery({
    queryKey: ["/platform"],
    queryFn: () => get("/platform"),
  });

  const { triggerConfetti } = useConfetti();
  const handleNext = useCallback(() => {
    navigate(V3ShareFriendsPage_URL);
  }, [navigate]);

  // Handle errors by redirecting to next page
  useEffect(() => {
    if (isActiveSignupRewardError) {
      handleNext();
    }
  }, [isActiveSignupRewardError, handleNext]);

  const rewardsEnabled = platform?.platformSettings?.rewardsEnabled;
  const firstName = user?.firstName || "";

  useEffect(() => {
    if (!rewardsEnabled && !isPlatformLoading) {
      handleNext();
    }
  }, [rewardsEnabled, activeSignupReward, handleNext, isPlatformLoading]);

  useEffect(() => {
    if (!activeSignupReward && !isActiveSignupRewardLoading) {
      handleNext();
    }
  }, [handleNext, activeSignupReward, isActiveSignupRewardLoading]);

  useEffect(() => {
    if (isActiveSignupRewardLoading || isPlatformLoading) {
      return;
    }

    const startDelay = setTimeout(() => {
      setProgress(100);
    }, 300);

    const rewardDelay = setTimeout(() => {
      setShowReward(true);
      triggerConfetti();
    }, 1500);

    return () => {
      clearTimeout(startDelay);
      clearTimeout(rewardDelay);
    };
  }, [
    rewardsEnabled,
    triggerConfetti,
    isActiveSignupRewardLoading,
    isPlatformLoading,
  ]);

  const renderHeader = () => (
    <div className="flex w-full shrink-0 flex-col gap-6">
      <div className="flex w-full flex-col items-center justify-center gap-4 text-center">
        <p className="w-full shrink-0 font-serif text-[30px] font-medium italic leading-[32px] text-[#091f24]">
          {firstName
            ? `${firstName}, enjoy your first reward.`
            : "Enjoy your first reward."}
        </p>
      </div>
    </div>
  );

  if (isActiveSignupRewardLoading) {
    return null;
  }

  const renderRewardCard = () =>
    showReward && (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="flex w-full shrink-0 flex-col items-center overflow-hidden rounded-lg shadow-[0px_2px_10px_0px_rgba(0,0,0,0.06)]"
      >
        <div className="flex w-full shrink-0 flex-col gap-2.5 bg-white/40 px-4 pb-3 pt-4">
          <div className="inline-flex shrink-0 items-center gap-2.5 self-start rounded bg-brand-color px-1.5 py-1">
            <p
              className="shrink-0 whitespace-nowrap font-sans text-[10px] font-bold uppercase leading-[12px] tracking-[0.4px] text-white"
              style={{ fontVariationSettings: "'opsz' 14" }}
            >
              reward unlocked!
            </p>
          </div>

          <div className="flex w-full shrink-0 flex-col gap-1">
            <p
              className="w-full shrink-0 font-sans text-base font-normal leading-[22px] text-[#091f24]"
              style={{ fontVariationSettings: "'opsz' 14" }}
            >
              {activeSignupReward?.title}
            </p>
          </div>
        </div>
      </motion.div>
    );

  return (
    <MobileViewportContainer
      header={<OnboardingBackButton prev="/signin/source" />}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: ANIMATION_DURATION, delay: 0.1 }}
        className="flex w-full max-w-sm flex-col items-center gap-9"
      >
        <Icon svg={GiftboxV3} size="xlarge" color="brandColor" />

        {renderHeader()}

        <div className="w-full">
          <RewardProgressBar progress={progress} />
        </div>

        {renderRewardCard()}

        {showReward && (
          <Button fullWidth onClick={handleNext}>
            Continue
          </Button>
        )}
      </motion.div>
    </MobileViewportContainer>
  );
}
